<script>
import {Link} from "@inertiajs/vue3";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import default_img from "@img/defaults/product-not-found.webp";

export default {
    name: "ImageSearchResultItem",
    props: {
        data: {
            type: Object,
            required: true,
        }
    },
    components: {
        NumberFormatComponent,
        ImageLoader,
        Link,
    },
    computed: {
        validPrices() {
            if (!Array.isArray(this.data?.prices)) {
                return [];
            }

            return this.data.prices.filter(item => {
                return item
                    && item.base_price !== null
                    && item.base_price !== undefined
                    && Number(item.base_price) > 0;
            });
        },

        displayPriceRow() {
            if (!this.validPrices.length) {
                return null;
            }

            return [...this.validPrices].sort((a, b) => Number(a.base_price) - Number(b.base_price))[0];
        },

        displayPrice() {
            if (this.displayPriceRow) {
                return Number(this.displayPriceRow.base_price);
            }

            if (this.data?.min_price !== null && this.data?.min_price !== undefined) {
                return Number(this.data.min_price);
            }

            return null;
        },

        hasPriceRange() {
            if (this.data?.min_price === null || this.data?.min_price === undefined) {
                return false;
            }

            if (this.data?.max_price === null || this.data?.max_price === undefined) {
                return false;
            }

            return Number(this.data.min_price) !== Number(this.data.max_price);
        },

        unitLabel() {
            const priceType = this.displayPriceRow?.price_type;

            if (!priceType) {
                return '';
            }

            return priceType.short_title || priceType.title || '';
        },

        categoryLine() {
            const parts = [];

            if (this.data?.category?.title) {
                parts.push(this.data.category.title);
            }

            if (this.data?.sub_category?.title) {
                parts.push(this.data.sub_category.title);
            }

            return parts.join(' • ');
        },

        imageSrc() {
            const image = this.data?.images?.[0];
            return image ? image : default_img;
        },
    },
}
</script>

<template>
    <div class="relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition md:hover:border-baseColor">
        <Link :href="route('product', {slug: data.slug})" class="relative w-full">
            <div class="relative w-full overflow-hidden border-b border-gray-200 bg-white">
                <div
                    v-if="data.is_hit"
                    class="absolute right-0 top-0 z-10 rounded-bl-md bg-[#c52314] px-3 py-1 text-[11px] font-bold leading-5 text-white"
                >
                    ХИТ ПРОДАЖ
                </div>

                <ImageLoader
                    :title="data.title"
                    :src="imageSrc"
                    class="min-h-[180px]"
                    imgClasses="w-full h-full object-cover object-center max-h-[220px]"
                />
            </div>
        </Link>

        <div class="flex min-w-0 grow flex-col px-3 pt-4 pb-3">
            <div
                class="w-min text-nowrap rounded px-2 py-0.5 text-[11px]"
                :class="[data.available ? 'bg-emerald-600 text-white' : 'bg-amber-400 text-black']"
            >
                {{ data.available ? 'В наличии' : 'Под заказ' }}
            </div>

            <Link
                :href="route('product', {slug: data.slug})"
                class="mt-2 text-sm font-medium leading-5 text-gray-900 transition hover:text-navHoverColor md:text-base line-clamp-2"
            >
                {{ data.title }}
            </Link>

            <div
                v-if="categoryLine"
                class="mt-1 text-[12px] leading-4 text-gray-500 line-clamp-1"
            >
                {{ categoryLine }}
            </div>

            <div class="mt-auto pt-4">
                <div class="text-sm leading-tight text-gray-500" v-if="displayPriceRow?.price_type?.title">
                    {{ displayPriceRow.price_type.title }}
                </div>

                <div class="mt-1 text-sm leading-tight text-gray-900">
                    <template v-if="displayPrice !== null">
                        <span v-if="hasPriceRange" class="mr-1">от</span>

                        <NumberFormatComponent
                            :number="displayPrice"
                            class="inline-block text-[22px] font-semibold"
                        />

                        <span class="ml-1 text-[15px] font-medium">
                            ₽<template v-if="unitLabel">/{{ unitLabel }}</template>
                        </span>
                    </template>

                    <span v-else class="text-[20px] font-semibold">
                        Договорная
                    </span>
                </div>

                <div class="mt-4">
                    <Link
                        :href="route('product', {slug: data.slug})"
                        class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-baseColor text-sm font-semibold text-black transition hover:bg-baseColor/80"
                    >
                        Посмотреть продукт
                    </Link>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.line-clamp-1 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 1;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
}
</style>
